import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../LangContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Linkedin, Facebook, Twitter, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LK = { EN: "en", FR: "fr", NL: "nl" };

export default function WhySportPage() {
    const { t, lang } = useLang();
    const [items, setItems] = useState([]);
    const langKey = LK[lang] || "en";
    const tx = (loc) => loc?.[langKey] || loc?.en || "";

    useEffect(() => {
        axios.get(`${API}/why-sport`).then((r) => setItems(r.data)).catch(() => {});
        window.scrollTo(0, 0);
    }, []);

    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareText = encodeURIComponent(t.why.title);
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied");
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <div className="bg-ink text-white grain min-h-screen" data-testid="why-sport-page">
            <Header />

            {/* Hero */}
            <section className="pt-32 md:pt-44 px-6 md:px-12 pb-16 relative overflow-hidden">
                <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-volt/10 blur-3xl rounded-full pointer-events-none" />
                <div className="relative max-w-5xl mx-auto">
                    <div className="mb-8">
                        <Link
                            to="/"
                            data-testid="why-back"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-400 hover:text-volt"
                        >
                            <ArrowLeft size={14} /> {t.why.back}
                        </Link>
                    </div>
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-volt">— {t.why.label}</span>
                    <h1 className="font-graffiti text-5xl md:text-7xl lg:text-8xl text-white leading-[0.92] tracking-tight mt-4">
                        {t.why.title}
                    </h1>
                    <p className="mt-8 text-zinc-300 text-xl leading-relaxed max-w-3xl">{t.why.manifesto_intro}</p>

                    {/* Share bar */}
                    <div className="mt-10 flex items-center gap-3">
                        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">— Share</span>
                        <a
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer"
                            data-testid="why-share-linkedin"
                            className="w-10 h-10 border border-white/10 hover:border-volt hover:text-volt flex items-center justify-center text-white transition-colors"
                            aria-label="Share on LinkedIn"
                        >
                            <Linkedin size={16} />
                        </a>
                        <a
                            href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer"
                            data-testid="why-share-twitter"
                            className="w-10 h-10 border border-white/10 hover:border-volt hover:text-volt flex items-center justify-center text-white transition-colors"
                            aria-label="Share on X/Twitter"
                        >
                            <Twitter size={16} />
                        </a>
                        <a
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                            target="_blank" rel="noreferrer"
                            data-testid="why-share-facebook"
                            className="w-10 h-10 border border-white/10 hover:border-volt hover:text-volt flex items-center justify-center text-white transition-colors"
                            aria-label="Share on Facebook"
                        >
                            <Facebook size={16} />
                        </a>
                        <button
                            onClick={copyLink}
                            data-testid="why-share-copy"
                            className="w-10 h-10 border border-white/10 hover:border-volt hover:text-volt flex items-center justify-center text-white transition-colors"
                            aria-label="Copy link"
                        >
                            <LinkIcon size={16} />
                        </button>
                    </div>
                </div>
            </section>

            {/* Manifesto sections */}
            <main className="pb-24">
                {items.map((p, i) => (
                    <section
                        key={p.id}
                        id={p.slug}
                        data-testid={`why-snippet-${i}`}
                        className={`px-6 md:px-12 py-20 md:py-28 ${i % 2 === 0 ? "bg-ink" : "bg-inksurface"} border-t border-white/5`}
                    >
                        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                                className={`lg:col-span-7 ${i % 2 === 0 ? "" : "lg:order-2"}`}
                            >
                                <div className="font-mono text-xs text-volt tracking-[0.3em] mb-6">
                                    — {String(p.order).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                                </div>
                                <h2 className="font-graffiti text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
                                    {tx(p.title)}
                                </h2>
                                <p className="mt-8 text-zinc-200 text-lg md:text-xl leading-relaxed max-w-2xl">
                                    {tx(p.body)}
                                </p>
                                {p.cta_text ? (
                                    <a
                                        href={p.cta_link || "#donate"}
                                        data-testid={`why-snippet-cta-${i}`}
                                        className="btn-volt inline-flex items-center gap-2 mt-10 px-6 py-3 text-xs"
                                    >
                                        {p.cta_text} <ArrowRight size={14} />
                                    </a>
                                ) : null}
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, scale: 0.96 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7 }}
                                className={`lg:col-span-5 ${i % 2 === 0 ? "" : "lg:order-1"}`}
                            >
                                {p.image_url ? (
                                    <div className="aspect-[4/5] w-full overflow-hidden border border-white/10">
                                        <img src={p.image_url} alt={tx(p.title)} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                                    </div>
                                ) : (
                                    <div className="aspect-[4/5] w-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center border border-white/10">
                                        <div className="font-graffiti text-9xl text-volt/30">{String(p.order).padStart(2, "0")}</div>
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    </section>
                ))}

                {/* Closing CTA */}
                <section className="px-6 md:px-12 py-24 bg-ink text-center border-t border-white/10">
                    <div className="max-w-3xl mx-auto">
                        <h2 className="font-graffiti text-4xl md:text-6xl text-white leading-[0.95] tracking-tight">
                            Now you know why.
                        </h2>
                        <p className="text-zinc-300 text-lg mt-6">
                            The next step is making it happen for more kids.
                        </p>
                        <div className="mt-10 flex flex-wrap justify-center gap-4">
                            <a href="/#donate" data-testid="why-final-donate" className="btn-volt px-8 py-4 text-sm">
                                Fund a Rookie →
                            </a>
                            <a href="/#contact" data-testid="why-final-contact" className="btn-ghost px-8 py-4 text-sm uppercase font-bold tracking-wider">
                                Become a partner
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
